import React, { useCallback, useState, useContext, useEffect } from 'react';

// utils
import { useNavigate } from 'react-router-dom';
import { useRazorpay } from 'react-razorpay';

// helper
import { getRandomColorsBassedOnLetters } from 'helper/colors';
import { cn } from '../../../helper/cn';
import { isValidGST, numberOnly, textOnly } from '../../../helper/validation';

// components
import {
  currentBrandWhatsappConnected,
  billingPlanPricingApi,
} from '../../../api/Api';
import Loader from '../../../components/commonComponents/Loader/Index';
import { dateConverter } from '../../../helper/dateConverter';
import useToggle from '../../../hooks/useToggle';
import {
  updateCurrentBrand,
  updateToggleToast,
} from '../../../reduxToolkit/appSlice';
import { useAspDispatch, useAspSelector } from '../../../test/jest-redux-hooks';
import {
  brandsInfoApi,
  creditsSendApi,
  transactionsApi,
  transactionsResponseApi,
  brandUserEditApi,
  setupApi,
  creditsCancelApi,
} from '../api/Api';
import OnBoardingModal from './OnBoardingModal';
import OverviewCarousel from './OverviewCarousel';
import PricingPlanModal from './PricingPlanModal';
import CheckoutModal from './CheckoutModal';
import { razorpay_key } from '../../../api/ApiConfig';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../components/commonComponents/gxAntdToast/toastUtils';
import NotificationContext from '../../../providers/NotificationProvider';
import { inputsArray } from '../constants/home';
import { cityListApi, stateListApi } from '../../crm/api/Api';
import { category as defaultCategory } from '../../auth/components/onboarding/BrandCreation';

export default function AccountCard({
  id,
  name,
  image,
  metrics,
  loadData,
  lastLogin,
  billing_plan_code,
  billing_plan_name,
  currency_symbol,
  total_amount,
  brandUsers,
  end_date,
  created_at,
  status,
  featureFlags,
}) {
  const countsList = [
    {
      id: 1,
      label: 'No. of brands',
      value: metrics?.total_brands,
    },
    {
      id: 2,
      label: 'No. of people',
      value: metrics?.total_users,
    },
    {
      id: 3,
      label: 'Active brands',
      value: metrics?.active_brands,
    },
    {
      id: 4,
      label: 'In-active brands',
      value: metrics?.inactive_brands,
    },
    {
      id: 5,
      label: 'Pending Invoices',
      value: metrics?.pendinng_invoices,
    },
  ];

  const navigate = useNavigate();
  const dispatch = useAspDispatch();

  const { currentAccount, toggleToast } = useAspSelector((state) => state?.app);

  const [openModal, setOpenModal] = useToggle(false);
  const [isSelecting, setIsSelecting] = useToggle(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  // States for Checkout Flow (Renewal)
  const { Razorpay } = useRazorpay();
  const gxNotification = useContext(NotificationContext);
  const [createLoader, setCreateLoader] = useState(false);
  const [creditsResponse, setCreditsResponse] = useState(null);
  const [fullBrandData, setFullBrandData] = useState({});
  const [dynamicInputs, setDynamicInputs] = useState([]);
  const [editInfo, setEditInfo] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);

  const updateData = useCallback(({ datas, value }) => {
    if (!value) return datas;
    const res = datas?.map((data) => {
      const { key, secondaryKey } = data;
      if (secondaryKey) {
        const data_id =
          secondaryKey === 'country'
            ? value?.[key]?.[0]?.['country_id']
            : secondaryKey === 'city'
            ? value?.[key]?.[0]?.['city_id']
            : secondaryKey === 'province'
            ? value?.[key]?.[0]?.['province_id']
            : '';
        return {
          ...data,
          value: value?.[key]?.[0]?.[secondaryKey],
          locations_id: data_id,
        };
      }
      return { ...data, value: value?.[key] };
    });
    return res;
  }, []);

  useEffect(() => {
    if (fullBrandData && Object.keys(fullBrandData).length > 0) {
      setDynamicInputs(
        updateData({ datas: inputsArray, value: fullBrandData }),
      );

      const location = fullBrandData.locations_with_names?.[0];
      if (location?.country_id) {
        stateListApi(location.country_id).then((res) => {
          if (res?.status === 200) {
            const states = res?.data?.data?.map((item) => ({
              label: item?.name,
              value: item?.name,
              id: item?.id,
            }));
            setStateList(states);
          }
        });
      }
      if (location?.province_id) {
        cityListApi(location.province_id).then((res) => {
          if (res?.status === 200) {
            const cities = res?.data?.data?.map((item) => ({
              label: item?.name,
              value: item?.name,
              id: item?.id,
            }));
            setCityList(cities || []);
          }
        });
      }
    }
  }, [fullBrandData, updateData]);

  const handleBrandChange = useCallback(({ id, value, locations_id }) => {
    setDynamicInputs((prev) => {
      const updated = prev.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            value,
            locations_id: locations_id || item.locations_id,
          };
        }
        return item;
      });

      if (id === 7) {
        // State change
        setCityList([]);
        if (locations_id) {
          cityListApi(locations_id).then((res) => {
            if (res?.status === 200) {
              const cities = res?.data?.data?.map((item) => ({
                label: item?.name,
                value: item?.name,
                id: item?.id,
              }));
              setCityList(cities || []);
            }
          });
        }
        return updated.map((item) =>
          item.id === 8 ? { ...item, value: '', locations_id: null } : item,
        );
      }

      return updated;
    });
  }, []);

  const buttonEnable = useCallback(() => {
    if (!dynamicInputs || dynamicInputs.length === 0) return false;

    return dynamicInputs.every((input) => {
      if (input?.id === 10) {
        if (!input?.value) return true;
        return isValidGST(input?.value);
      }
      if (input?.id === 8) return true;
      const isValid =
        input?.value &&
        input?.value !== undefined &&
        input?.value !== null &&
        input?.value !== 'Unknown';

      if (input?.id === 9) {
        return isValid && input.value?.length >= 5;
      }

      return isValid;
    });
  }, [dynamicInputs]);

  const handleUpdate = async () => {
    const payLoad = {
      brand_name: dynamicInputs?.[0]?.value,
      website_url: dynamicInputs?.[2]?.value,
      category: dynamicInputs?.[1]?.value,
      gst_number: dynamicInputs?.[8]?.value,
      address: {
        id: fullBrandData?.locations_with_names?.[0]?.location,
        address_line_1: dynamicInputs?.[3]?.value,
        country_id: dynamicInputs?.[4]?.locations_id,
        province_id: dynamicInputs?.[5]?.locations_id,
        city_id: dynamicInputs?.[6]?.locations_id || 1,
        postal_index_code: dynamicInputs?.[7]?.value,
      },
    };

    if (buttonEnable()) {
      setCreateLoader(true);
      const res = await brandUserEditApi(id, payLoad);
      setCreateLoader(false);
      if (res?.status === 200) {
        showSuccessToast(gxNotification, 'Brand info updated');
        setEditInfo(false);
        const brandRes = await brandsInfoApi(id);
        if (brandRes?.status === 200) {
          setFullBrandData(brandRes.data);
        }
      } else {
        showErrorToast(gxNotification, res?.response?.data?.error);
      }
    }
  };

  const handleRenewSubscription = () => {
    setCreateLoader(true);
    const payLoad = {
      brand_id: id,
      transaction_type: 'SUBSCRIPTION',
      billing_plan_name: billing_plan_code,
      billing_cycle: 'monthly', // Defaulting to monthly for renewal if not specified
    };

    creditsSendApi(payLoad).then((res) => {
      if (res?.status === 200) {
        setCreditsResponse(res?.data?.credit_item);
        brandsInfoApi(id).then((brandRes) => {
          setCreateLoader(false);
          if (brandRes?.status === 200) {
            setFullBrandData(brandRes.data);
            setEditInfo(false);
            setIsCheckoutModalOpen(true);
          }
        });
      } else {
        setCreateLoader(false);
        showErrorToast(gxNotification, res?.response?.data?.error);
      }
    });
  };

  const handlePayment = (updatedBillSummary) => {
    setCreateLoader(true);
    const billData = updatedBillSummary || creditsResponse;
    const transactionItemId = billData?.credit_item?.id || billData?.id;

    const payLoad = {
      brand_id: id,
      transaction_item_id: transactionItemId,
      type: 'SUBSCRIPTIONS',
    };

    transactionsApi(payLoad).then((res) => {
      if (res?.status === 200) {
        setCreateLoader(false);
        const options = {
          key: razorpay_key,
          amount: res?.data?.transaction?.total_cost * 100,
          currency: res?.data?.transaction?.currency,
          order_id: res?.data?.transaction?.razerpay_order_id,
          notes: { transaction_id: transactionItemId },
          handler: (response) => {
            transactionsResponseApi(response).then((res) => {
              if (res?.status === 200) {
                showSuccessToast(gxNotification, 'Payment Success');
                setIsCheckoutModalOpen(false);
                if (loadData) loadData();
                setupApi(id);
              }
            });
          },
        };
        const rzp = new Razorpay(options);
        rzp.open();
      } else {
        setCreateLoader(false);
        showErrorToast(gxNotification, res?.response?.data?.error);
      }
    });
  };

  const handleBackPlan = () => {
    const payLoad = {
      brand_id: id,
      transaction_item_id: creditsResponse?.id,
    };
    creditsCancelApi(payLoad).then(() => {
      setIsCheckoutModalOpen(false);
    });
  };

  const colors = getRandomColorsBassedOnLetters(name);
  const date = dateConverter(lastLogin);
  const lastloginBrand = `${date?.monthInShort} ${date?.date},${date?.year}`;

  const handleNavigate = (id) => {
    brandsInfoApi(id)
      .then((res) => {
        if (res?.status === 200) {
          const brand = currentAccount?.brands?.find(
            (item) => item?.brand_id === id,
          );

          dispatch(
            updateCurrentBrand({
              ...brand,
              feature_flag: featureFlags,
            }),
          );

          navigate(`/profile/account/overview/${id}/brand_info`, {
            state: { brandsData: res?.data },
          });
        } else {
          dispatch(
            updateToggleToast([
              ...toggleToast,
              {
                id: toggleToast?.length + 1,
                content: res?.response?.data?.error,
                status: 'Error',
                duration: '',
              },
            ]),
          );
        }
      })
      .catch((error) => {
        console.error('Error fetching brand info:', error);
      });
  };

  const handleBrandSelect = useCallback(
    (id) => {
      setIsSelecting(true);
      const brand = currentAccount?.brands?.find(
        (item) => item?.brand_id === id,
      );

      // 1. Update Redux IMMEDIATELY with known data
      // This prevents the "stale state" flicker when navigating
      dispatch(
        updateCurrentBrand({
          ...brand,
          brand_id: id,
          brand_status: status,
          brand_name: name,
          brand_billing_plan_code: billing_plan_code,
          brand_billing_plan_name: billing_plan_name,
          feature_flag: featureFlags,
          is_whatsapp_connected: true, // Default to false, will update after API
        }),
      );

      // 2. Navigate IMMEDIATELY
      navigate('/user/home');

      // 3. Fetch extra data in background (WhatsApp status)
      if (id) {
        currentBrandWhatsappConnected(id)
          .then((res) => {
            const wap = res?.data?.is_whatsapp_active;

            // Update state again with the fresh WhatsApp status
            dispatch(
              updateCurrentBrand({
                ...brand,
                brand_id: id,
                brand_status: status,
                brand_name: name,
                brand_billing_plan_code: billing_plan_code,
                brand_billing_plan_name: billing_plan_name,
                is_whatsapp_connected: wap,
                feature_flag: featureFlags,
              }),
            );
          })
          .finally(() => {
            setIsSelecting(false);
          });
      } else {
        setIsSelecting(false);
      }
    },
    [
      currentAccount,
      dispatch,
      navigate,
      status,
      name,
      billing_plan_code,
      billing_plan_name,
      featureFlags,
    ],
  );

  const utcDate = new Date(end_date);
  const currentDate = new Date();
  const differenceInMs = utcDate.getTime() - currentDate.getTime();
  const differenceInDays = Math.floor(differenceInMs / (1000 * 60 * 60 * 24));

  // Determine notification message and style based on priority
  const getNotification = () => {
    // Priority 1: Onboarding
    if ([2, 3, 4, 5].includes(status)) {
      return {
        message: 'Onboarding...',
        bgClass: 'bg-[#B49FF9] border-[#B49FF9]',
      };
    }

    // Priority 2: Subscription expired
    if (differenceInDays <= 0) {
      return {
        message: 'Subscription expired!',
        bgClass: 'bg-[#FF543E] border-[#FF543E]',
      };
    }

    // Priority 3: Subscription expiring soon
    if (differenceInDays < 3 && differenceInDays > 0) {
      return {
        message: 'Subscription expires soon!',
        bgClass: 'bg-[#F6A723] border-[#F6A723]',
      };
    }

    // Priority 4: Credits exhausted
    if (total_amount === null) {
      return {
        message: 'Credits exhausted!',
        bgClass: 'bg-[#FF543E] border-[#FF543E]',
      };
    }

    // Priority 5: Credits running low
    if (total_amount < 1000 && total_amount !== null) {
      return {
        message: 'Credits running low!',
        bgClass: 'bg-[#F6A723] border-[#F6A723]',
      };
    }

    // No notification
    return null;
  };

  const expiryNotification = getNotification();

  return (
    <div className="">
      {/* Fixed height banner area - Always 40px for consistency */}
      <div className="h-[25px] w-full">
        {billing_plan_code && expiryNotification ? (
          <div
            className={`h-full w-fit flex items-center text-white font15px-500 ${expiryNotification.bgClass} rounded-t-xl px-2 py-1`}
          >
            <span className="truncate">{expiryNotification.message}</span>
          </div>
        ) : null}
      </div>

      <div
        className={`group w-full relative bg-white pt-4 pb-10 border ${
          expiryNotification
            ? `${expiryNotification.bgClass.replace(
                'bg-',
                'border-',
              )} rounded-bl-xl rounded-tr-xl rounded-br-xl`
            : 'border-[var(--border-input)] rounded-xl'
        } cursor-pointer`}
        onClick={() => {
          // Don't navigate if modals are open
          if (isPricingModalOpen || isCheckoutModalOpen) return;

          if (currentAccount?.role !== 'Account Manager') {
            handleBrandSelect(id);
          }
        }}
      >
        <div className="w-full h-full">
          <div
            className="w-full flex items-center gap-4 px-4 pb-5"
            // onClick={(e) => {
            //   clearSearch();
            //   // handleNavigate(id);
            // }}
          >
            <div
              className={`w-[3.5vw] h-[3.5vw] rounded-xl relative  ${
                image ? 'border-2' : 'border-none'
              }`}
            >
              {image !== null ? (
                <img
                  src={image !== null ? image : ''}
                  alt=""
                  className="w-full h-full object-cover p-1 rounded-xl"
                />
              ) : (
                <p
                  className="text-white bg-[#8796AF] w-full h-full flex justify-center items-center rounded-xl text-[2vw]"
                  style={{
                    color: colors.color,
                    backgroundColor: colors.backgroundColor,
                  }}
                >
                  {name.charAt(0).toUpperCase()}
                </p>
              )}
            </div>
            <div className="flex flex-col justify-center gap-1">
              <h4 className="w-full max-w-[200px] truncate text-base weight-medium capitalize">
                {name}
              </h4>
              <span className="text-[#6564CA] font12px-500 italic">
                {lastLogin !== null && `(Last login: ${lastloginBrand})`}
              </span>
              {/* {ACCOUNT_LEVEL_ACCESS(role) && (
              <p className="w-fit rounded text-xs font-semibold px-2.5 py-1 bg-[var(--BG-50)] text-[var(--font-600)]">
                {role}
              </p>
            )} */}
            </div>
          </div>
          <div className="w-full">
            <div className="w-full h-full  relative">
              <OverviewCarousel
                data={countsList}
                // viewAccount={viewAccount}
                billing_plan_code={billing_plan_code}
                currency_symbol={currency_symbol}
                total_amount={total_amount}
                brandUsers={brandUsers}
                differenceInDays={differenceInDays}
                created_at={created_at}
                className="group-hover:!flex hidden"
                name={name}
              />
            </div>
            <div
              className="px-4 flex-column gap-3 pb-10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Top row: Goto Brand & View Info buttons */}
              <div className="flex-row align-center gap-4">
                {/* goto brand button */}
                {currentAccount?.role !== 'Account Manager' ? (
                  <button
                    type="button"
                    className="w-full flex-row items-center justify-center font16px-500 border border-[var(--new-primary)] text-[var(--new-primary)] b-radius-6 px-4 py-2"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleBrandSelect(id);
                    }}
                  >
                    {isSelecting ? (
                      <div className=" flex items-center justify-center">
                        <Loader Width={20} Height={20} />
                      </div>
                    ) : (
                      'Goto Brand'
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="w-full font16px-500 bg-[var(--BG-50)] text-[#A9B1C1] b-radius-6 px-4 py-2 cursor-not-allowed"
                  >
                    Goto Brand
                  </button>
                )}
                {/* View Info button */}
                {currentAccount?.role === 'Account User' &&
                currentAccount?.account_type === 'Agency' ? (
                  <div />
                ) : (
                  <button
                    type="button"
                    className={`w-full font16px-500 text-white b-radius-6 px-4 py-2 bg-[var(--new-primary)]`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation(); // Prevent card click handler from firing
                      handleNavigate(id);
                    }}
                  >
                    View Info
                  </button>
                )}
              </div>

              {/* Bottom row: Upgrade/Renew Subscription button */}
              <button
                type="button"
                disabled={createLoader}
                className={`w-full flex items-center justify-center font16px-500 b-radius-6 px-4 py-2 ${
                  createLoader ? 'cursor-not-allowed opacity-70' : ''
                } ${
                  differenceInDays <= 5 &&
                  billing_plan_code?.toLowerCase() !== 'magnum'
                    ? 'bg-[#FF543E] text-white'
                    : 'text-white'
                }`}
                style={
                  billing_plan_code?.toLowerCase() === 'magnum'
                    ? { background: '#8B5CF6' }
                    : differenceInDays > 5
                    ? {
                        background:
                          'linear-gradient(115.45deg, #0ACD95 36.03%, #6940F2 83.89%)',
                      }
                    : {}
                }
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation(); // Prevent card click handler from firing
                  if (billing_plan_code?.toLowerCase() === 'magnum') {
                    window.open(
                      'https://calendar.app.google/eMb413uHfzqeUv5fA',
                      '_blank',
                      'noopener,noreferrer',
                    );
                  } else if (differenceInDays <= 5) {
                    handleRenewSubscription();
                  } else {
                    setIsPricingModalOpen(true);
                  }
                }}
              >
                {createLoader &&
                differenceInDays <= 5 &&
                billing_plan_code?.toLowerCase() !== 'magnum' ? (
                  <Loader Width={20} Height={20} />
                ) : billing_plan_code?.toLowerCase() === 'magnum' ? (
                  'Contact Sales'
                ) : differenceInDays <= 5 ? (
                  'Renew Subscription'
                ) : (
                  'Upgrade Subscription'
                )}
              </button>
            </div>
          </div>
        </div>
        {/* <Tooltip title="Account settings">
        <div
          className="absolute top-5 right-5 z-50"
          onClick={(e) => {
            clearSearch();
            navigate(`${id}/edit`);
          }}
        >
          <Settings width={16} height={16} strokeWidth="2" />
        </div>
      </Tooltip> */}
        <OnBoardingModal
          open={openModal}
          setOpen={setOpenModal}
          creation_type={'brand'}
          accountData={{ id: id }}
          loadData={loadData}
        />

        {/* Pricing Modal for Upgrade */}
        <PricingPlanModal
          open={isPricingModalOpen}
          setOpen={setIsPricingModalOpen}
          brandId={id}
          onSuccess={() => {
            setIsPricingModalOpen(false);
            if (loadData) loadData();
          }}
        />

        {/* Note: For renew subscription, we use CheckoutModal directly
            after initializing the transaction in handleRenewSubscription. */}
        <CheckoutModal
          open={isCheckoutModalOpen}
          setOpen={setIsCheckoutModalOpen}
          isPlanSelected={true}
          handleBackPlan={handleBackPlan}
          locationError={locationError}
          brandsData={fullBrandData}
          creditsResponse={creditsResponse}
          dynamicInputs={dynamicInputs}
          editInfo={editInfo}
          setEditInfo={setEditInfo}
          handleUpdate={handleUpdate}
          handleBrandChange={handleBrandChange}
          input={
            'font14px-500 text-[var(--textBlack)] w-full py-2 bg-transparent outline-none'
          }
          inputContainer={'w-full h-11 flex items-center rounded-lg mt-5'}
          buttonClass={(editInfoState) => {
            if (editInfoState && buttonEnable())
              return 'bg-[#0F9960] text-white rounded-lg px-10 py-1.5 mb-20 font14px-600';
            if (editInfoState && !buttonEnable())
              return 'border border-[var(--border-input)] bg-[var(--BG-50)] text-[var(--disable-color)] rounded-lg px-10 py-1.5 mb-20 font14px-600 cursor-not-allowed';
            return 'bg-[#E7FDF3] text-[#0F9960] rounded-lg px-10 py-1.5 mb-20 font14px-600';
          }}
          category={defaultCategory}
          stateList={stateList}
          cityList={cityList}
          buttonEnable={buttonEnable}
          handlePayment={handlePayment}
          currentAccount={currentAccount}
          createLoader={createLoader}
        />
      </div>
    </div>
  );
}
